/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging";

/**
 * webOS device info returned by `webOS.deviceInfo()`.
 */
interface IWebOsDeviceInfo {
  modelName?: string;
  modelNameAscii?: string;
  version?: string;
  versionMajor?: number;
  versionMinor?: number;
  versionDot?: number;
  sdkVersion?: string;
  screenWidth?: number;
  screenHeight?: number;
  uhd?: boolean;
  uhd8K?: boolean;
  oled?: boolean;
  ddrSize?: string;
  hdr10?: boolean;
  dolbyVision?: boolean;
  dolbyAtmos?: boolean;
  brandName?: string;
  manufacturer?: string;
}

/**
 * Minimal webOS service request interface for Luna calls.
 */
interface IWebOsServiceRequest {
  method: string;
  parameters: Record<string, unknown>;
  onSuccess: (response: Record<string, unknown>) => void;
  onFailure: (error: { errorCode: number; errorText: string }) => void;
}

/**
 * Minimal webOS SDK interface — subset used by Unity Web.
 */
interface IWebOS {
  deviceInfo: (callback: (info: IWebOsDeviceInfo) => void) => void;
  fetchAppInfo: (
    callback: (info: Record<string, unknown> | undefined) => void,
    path?: string
  ) => void;
  fetchAppRootPath: () => string;
  platformBack: () => void;
  service: {
    request: (uri: string, params: IWebOsServiceRequest) => void;
  };
}

/**
 * Extended Window with webOS globals.
 */
interface IWindowWebOs extends Window {
  webOS?: IWebOS;
  appInfo?: Record<string, unknown>;
  deviceId?: string;
  deviceInfo?: string;
  getOSVersion?: () => string;
}

const TAG: string = "webOsDeviceInit";

/**
 * Chrome-version-to-webOS-version mapping.
 * Used to derive the webOS version from the user agent string.
 */
const CHROME_TO_WEBOS: ReadonlyArray<[string, string]> = [
  ["Chrome/132", "webOS TV 26"],
  ["Chrome/120", "webOS TV 25"],
  ["Chrome/108", "webOS TV 24"],
  ["Chrome/94", "webOS TV 23"],
  ["Chrome/87", "webOS TV 22"],
  ["Chrome/79", "webOS TV 6.0"],
  ["Chrome/68", "webOS TV 5.0"],
  ["Chrome/53", "webOS TV 4.5"]
];

/**
 * Derive the webOS version from the browser user agent.
 *
 * LG webOS TVs embed a specific Chrome version per platform release.
 * This function checks for the `Web0S` marker in the UA and maps
 * the Chrome version to the corresponding webOS version.
 *
 * @returns The webOS version string (e.g. `"webOS 6.0"`), or
 *          `"Unknown"` if the UA does not match any known version.
 */
function detectWebOsVersion(): string {
  const ua: string = navigator.userAgent;

  if (ua.indexOf("Web0S") === -1) {
    return "Unknown";
  }

  for (let i: number = 0; i < CHROME_TO_WEBOS.length; i++) {
    if (ua.indexOf(CHROME_TO_WEBOS[i][0]) !== -1) {
      return CHROME_TO_WEBOS[i][1];
    }
  }

  return "Unknown";
}

/**
 * Timeout in milliseconds for Luna service calls.
 */
const LUNA_TIMEOUT_MS: number = 5000;

/**
 * Guard that ensures an async Luna/SDK callback settles exactly once.
 *
 * Encapsulates the repeated pattern of "settled flag + timeout + clearTimeout"
 * used by `fetchDeviceId`, `fetchDeviceInfo`, and `fetchAppInfoData`.
 */
interface ISettleGuard {
  /** Attempt to settle. Returns `true` if this was the first call, `false` if already settled. */
  trySettle: () => boolean;
}

function createSettleGuard(fnName: string, resolve: () => void): ISettleGuard {
  let settled: boolean = false;
  const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
    if (!settled) {
      settled = true;
      Log.w(TAG, fnName + " timed out after " + LUNA_TIMEOUT_MS + "ms.");
      resolve();
    }
  }, LUNA_TIMEOUT_MS);

  return {
    trySettle: (): boolean => {
      if (settled) {
        return false;
      }
      settled = true;
      clearTimeout(timer);
      return true;
    }
  };
}

/**
 * Minified webOSTVjs v1.1.0 SDK — copied from TMIS
 * `platforms/webos/SDK/webOSTVjs-1.1.0/webOSTV.js`.
 *
 * This is the same code that TMIS `injectdevicejs.js` injected as an
 * inline `<script>`. It bootstraps `window.webOS` from the native
 * `PalmServiceBridge` and `PalmSystem` objects provided by the webOS
 * browser engine (WebAppManager).
 *
 * If this version needs to be updated, replace the string below with
 * the contents of the newer `webOSTV.js` release.
 */
const WEBOS_SDK_SOURCE: string =
  'window.webOS=function(e){function t(r){if(n[r])return n[r].exports;var o=n[r]={i:r,l:!1,exports:{}};return e[r].call(o.exports,o,o.exports,t),o.l=!0,o.exports}var n={};return t.m=e,t.c=n,t.i=function(e){return e},t.d=function(e,n,r){t.o(e,n)||Object.defineProperty(e,n,{configurable:!1,enumerable:!0,get:r})},t.n=function(e){var n=e&&e.__esModule?function(){return e.default}:function(){return e};return t.d(n,"a",n),n},t.o=function(e,t){return Object.prototype.hasOwnProperty.call(e,t)},t.p="",t(t.s=7)}([function(e,t,n){"use strict";Object.defineProperty(t,"__esModule",{value:!0});var r="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(e){return typeof e}:function(e){return e&&"function"==typeof Symbol&&e.constructor===Symbol&&e!==Symbol.prototype?"symbol":typeof e},o={};if("object"===("undefined"==typeof window?"undefined":r(window))&&window.PalmSystem)if(window.navigator.userAgent.indexOf("SmartWatch")>-1)o.watch=!0;else if(window.navigator.userAgent.indexOf("SmartTV")>-1||window.navigator.userAgent.indexOf("Large Screen")>-1)o.tv=!0;else{try{var i=JSON.parse(window.PalmSystem.deviceInfo||"{}"  );if(i.platformVersionMajor&&i.platformVersionMinor){var s=Number(i.platformVersionMajor),a=Number(i.platformVersionMinor);s<3||3===s&&a<=0?o.legacy=!0:o.open=!0}}catch(e){o.open=!0}window.Mojo=window.Mojo||{relaunch:function(){}},window.PalmSystem.stageReady&&window.PalmSystem.stageReady()}else o.unknown=!0;t.default=o},function(e,t,n){"use strict";function r(e,t){if(!(e instanceof t))throw new TypeError("Cannot call a class as a function")}Object.defineProperty(t,"__esModule",{value:!0});var o=Object.assign||function(e){for(var t=1;t<arguments.length;t++){var n=arguments[t];for(var r in n)Object.prototype.hasOwnProperty.call(n,r)&&(e[r]=n[r])}return e},i=function(){function e(e,t){for(var n=0;n<t.length;n++){var r=t[n];r.enumerable=r.enumerable||!1,r.configurable=!0,"value"in r&&(r.writable=!0),Object.defineProperty(e,r.key,r)}}return function(t,n,r){return n&&e(t.prototype,n),r&&e(t,r),t}}(),s={},a=function(e){var t=e;return"/"!==t.slice(-1)&&(t+="/"),t},c=t.LS2Request=function(){function e(){r(this,e),this.bridge=null,this.cancelled=!1,this.subscribe=!1}return i(e,[{key:"send",value:function(e){var t=e.service,n=void 0===t?"":t,r=e.method,i=void 0===r?"":r,c=e.parameters,u=void 0===c?{}:c,l=e.onSuccess,f=void 0===l?function(){}:l,d=e.onFailure,m=void 0===d?function(){}:d,p=e.onComplete,v=void 0===p?function(){}:p,h=e.subscribe,w=void 0!==h&&h;if(!window.PalmServiceBridge){var y={errorCode:-1,errorText:"PalmServiceBridge is not found.",returnValue:!1};return m(y),v(y),console.error("PalmServiceBridge is not found."),this}this.ts&&s[this.ts]&&delete s[this.ts];var b=o({},u);return this.subscribe=w,this.subscribe&&(b.subscribe=this.subscribe),b.subscribe&&(this.subscribe=b.subscribe),this.ts=Date.now(),s[this.ts]=this,this.bridge=new PalmServiceBridge,this.bridge.onservicecallback=this.callback.bind(this,f,m,v),this.bridge.call(a(n)+i,JSON.stringify(b)),this}},{key:"callback",value:function(){var e=arguments.length>0&&void 0!==arguments[0]?arguments[0]:function(){},t=arguments.length>1&&void 0!==arguments[1]?arguments[1]:function(){},n=arguments.length>2&&void 0!==arguments[2]?arguments[2]:function(){},r=arguments.length>3&&void 0!==arguments[3]?arguments[3]:"";if(!this.cancelled){var o={};try{o=JSON.parse(r)}catch(e){o={errorCode:-1,errorText:r,returnValue:!1}}var i=o,s=i.errorCode,a=i.returnValue;void 0!==s||!1===a?(o.returnValue=!1,t(o)):(o.returnValue=!0,e(o)),n(o),this.subscribe||this.cancel()}}},{key:"cancel",value:function(){this.cancelled=!0,null!==this.bridge&&(this.bridge.cancel(),this.bridge=null),this.ts&&s[this.ts]&&delete s[this.ts]}}]),e}(),u={request:function(){var e=arguments.length>0&&void 0!==arguments[0]?arguments[0]:"",t=arguments.length>1&&void 0!==arguments[1]?arguments[1]:{},n=o({service:e},t);return(new c).send(n)}};t.default=u},function(e,t,n){"use strict";Object.defineProperty(t,"__esModule",{value:!0});var r=t.fetchAppId=function(){return window.PalmSystem&&window.PalmSystem.identifier?window.PalmSystem.identifier.split(" ")[0]:""},o={};t.fetchAppInfo=function(e,t){if(0===Object.keys(o).length){var n=function(t,n){if(!t&&n)try{o=JSON.parse(n),e&&e(o)}catch(t){console.error("Unable to parse appinfo.json file for",r()),e&&e()}else e&&e()},i=new window.XMLHttpRequest;i.onreadystatechange=function(){4===i.readyState&&(i.status>=200&&i.status<300||0===i.status?n(null,i.responseText):n({status:404}))};try{i.open("GET",t||"appinfo.json",!0),i.send(null)}catch(e){n({status:404})}}else e&&e(o)},t.fetchAppRootPath=function(){var e=window.location.href;if("baseURI"in window.document)e=window.document.baseURI;else{var t=window.document.getElementsByTagName("base");t.length>0&&(e=t[0].href)}var n=e.match(new RegExp(".*://[^#]*/"));return n?n[0]:""},t.platformBack=function(){if(window.PalmSystem&&window.PalmSystem.platformBack)return window.PalmSystem.platformBack()}},function(e,t,n){"use strict";Object.defineProperty(t,"__esModule",{value:!0});var r=n(1),o=n(0),i=function(e){return e&&e.__esModule?e:{default:e}}(o),s={},a=function(e){if(0===Object.keys(s).length){try{var t=JSON.parse(window.PalmSystem.deviceInfo);s.modelName=t.modelName,s.version=t.platformVersion,s.versionMajor=t.platformVersionMajor,s.versionMinor=t.platformVersionMinor,s.versionDot=t.platformVersionDot,s.sdkVersion=t.platformVersion,s.screenWidth=t.screenWidth,s.screenHeight=t.screenHeight}catch(e){s.modelName="webOS Device"}s.screenHeight=s.screenHeight||window.screen.height,s.screenWidth=s.screenWidth||window.screen.width,i.default.tv&&(new r.LS2Request).send({service:"luna://com.webos.service.tv.systemproperty",method:"getSystemInfo",parameters:{keys:["firmwareVersion","modelName","sdkVersion","UHD"]},onSuccess:function(t){if(s.modelName=t.modelName||s.modelName,s.sdkVersion=t.sdkVersion||s.sdkVersion,s.uhd="true"===t.UHD,t.firmwareVersion&&"0.0.0"!==t.firmwareVersion||(t.firmwareVersion=t.sdkVersion),t.firmwareVersion){s.version=t.firmwareVersion;for(var n=s.version.split("."),r=["versionMajor","versionMinor","versionDot"],o=0;o<r.length;o+=1)try{s[r[o]]=parseInt(n[o],10)}catch(e){s[r[o]]=n[o]}}e(s)},onFailure:function(){e(s)}})}else e(s)};t.default=a},function(e,t,n){"use strict";Object.defineProperty(t,"__esModule",{value:!0});var r={isShowing:function(){return!!PalmSystem&&(PalmSystem.isKeyboardVisible||!1)}};t.default=r},function(e,t,n){"use strict";Object.defineProperty(t,"__esModule",{value:!0});var r=function(){var e={};if(window.PalmSystem){if(window.PalmSystem.country){var t=JSON.parse(window.PalmSystem.country);e.country=t.country,e.smartServiceCountry=t.smartServiceCountry}window.PalmSystem.timeZone&&(e.timezone=window.PalmSystem.timeZone)}return e};t.default=r},function(e,t,n){"use strict";Object.defineProperty(t,"__esModule",{value:!0});t.default="1.1.0"},function(e,t,n){"use strict";function r(e){return e&&e.__esModule?e:{default:e}}Object.defineProperty(t,"__esModule",{value:!0}),t.systemInfo=t.service=t.platform=t.platformBack=t.libVersion=t.keyboard=t.fetchAppRootPath=t.fetchAppInfo=t.fetchAppId=t.deviceInfo=void 0;var o=n(2),i=n(1),s=r(i),a=n(3),c=r(a),u=n(4),l=r(u),f=n(0),d=r(f),m=n(5),p=r(m),v=n(6),h=r(v);t.deviceInfo=c.default,t.fetchAppId=o.fetchAppId,t.fetchAppInfo=o.fetchAppInfo,t.fetchAppRootPath=o.fetchAppRootPath,t.keyboard=l.default,t.libVersion=h.default,t.platformBack=o.platformBack,t.platform=d.default,t.service=s.default,t.systemInfo=p.default}]);';

/**
 * Inject the webOSTVjs v1.1.0 SDK into the page.
 *
 * Mirrors the TMIS approach in `injectdevicejs.js`: creates a
 * `<script>` element with the minified webOSTVjs library as
 * `innerText` and appends it to `<head>`. The SDK bootstraps
 * `window.webOS` from the native `PalmServiceBridge` and
 * `PalmSystem` objects provided by the webOS runtime.
 *
 * @returns `true` if `window.webOS` is now available, `false` otherwise.
 */
function loadWebOsSdk(): boolean {
  const win = window as unknown as IWindowWebOs;

  // Already available (newer webOS runtime or previously loaded).
  if (win.webOS !== undefined && win.webOS !== null) {
    return true;
  }

  Log.i(TAG, "loadWebOsSdk(): Injecting webOSTVjs v1.1.0 SDK.");

  try {
    const script: HTMLScriptElement = document.createElement("script");
    script.type = "text/javascript";
    script.innerText = WEBOS_SDK_SOURCE;
    document.getElementsByTagName("head")[0].appendChild(script);
  } catch (e: unknown) {
    Log.e(TAG, "loadWebOsSdk(): Failed to inject webOSTVjs SDK script.", e);
    return false;
  }

  if (win.webOS !== undefined && win.webOS !== null) {
    Log.i(TAG, "loadWebOsSdk(): webOSTVjs v1.1.0 SDK loaded successfully.");
    return true;
  }

  Log.w(
    TAG,
    "loadWebOsSdk(): webOSTVjs SDK was injected but window.webOS was not created."
  );
  return false;
}

/**
 * Fetch the LG unique device ID (LGUDID) via Luna service.
 *
 * Sets `window.deviceId` with the retrieved LGUDID or
 * leaves it unchanged if the call fails.
 */
function fetchDeviceId(): Promise<void> {
  return new Promise<void>((resolve) => {
    const windowExt = window as unknown as IWindowWebOs;
    const webOS = windowExt.webOS;

    if (webOS === undefined || webOS === null) {
      Log.w(TAG, "fetchDeviceId(): webOS SDK not available, skipping.");
      resolve();
      return;
    }

    const guard: ISettleGuard = createSettleGuard("fetchDeviceId():", resolve);

    try {
      webOS.service.request("luna://com.webos.service.sm", {
        method: "deviceid/getIDs",
        parameters: { idType: ["LGUDID"] },
        onSuccess: (response: Record<string, unknown>) => {
          if (guard.trySettle()) {
            const idList = response["idList"] as
              | Array<{ idType: string; idValue: string }>
              | undefined;
            if (idList !== undefined && idList !== null && idList.length > 0) {
              windowExt.deviceId = idList[0].idValue;
              Log.i(TAG, "fetchDeviceId(): LGUDID retrieved successfully.");
            } else {
              Log.w(TAG, "fetchDeviceId(): idList empty in Luna response.");
            }
            resolve();
          }
        },
        onFailure: (error: { errorCode: number; errorText: string }) => {
          if (guard.trySettle()) {
            Log.w(
              TAG,
              "fetchDeviceId(): Luna call failed — " +
                "[" +
                String(error.errorCode) +
                "]: " +
                error.errorText
            );
            resolve();
          }
        }
      });
    } catch (e: unknown) {
      if (guard.trySettle()) {
        Log.e(TAG, "fetchDeviceId(): Exception calling Luna service.", e);
        resolve();
      }
    }
  });
}

/**
 * Fetch device info via `webOS.deviceInfo()`.
 *
 * Sets `window.deviceInfo` as a JSON string containing the
 * device model, SDK version, screen dimensions, and the deviceId.
 */
function fetchDeviceInfo(): Promise<void> {
  return new Promise<void>((resolve) => {
    const windowExt = window as unknown as IWindowWebOs;
    const webOS = windowExt.webOS;

    if (webOS === undefined || webOS === null) {
      Log.w(TAG, "fetchDeviceInfo(): webOS SDK not available, skipping.");
      resolve();
      return;
    }

    const guard: ISettleGuard = createSettleGuard(
      "fetchDeviceInfo():",
      resolve
    );

    try {
      webOS.deviceInfo((info: IWebOsDeviceInfo) => {
        if (guard.trySettle()) {
          const combined = {
            ...info,
            deviceId: windowExt.deviceId || ""
          };
          windowExt.deviceInfo = JSON.stringify(combined);
          Log.i(TAG, "fetchDeviceInfo(): Device info retrieved successfully.");
          resolve();
        }
      });
    } catch (e: unknown) {
      if (guard.trySettle()) {
        Log.e(TAG, "fetchDeviceInfo(): Exception calling webOS.deviceInfo.", e);
        resolve();
      }
    }
  });
}

/**
 * Fetch app info via `webOS.fetchAppInfo()`.
 *
 * First checks whether `window.appInfo` was already injected by the
 * shell (e.g. via `contentWindow.appInfo`).  If not, uses
 * `webOS.fetchAppRootPath()` to locate `appinfo.json` and reads it
 * via `webOS.fetchAppInfo()`.
 *
 * Note: `fetchAppInfo` performs an XHR GET, which only succeeds for
 * packaged (IPK) apps.  When Unity Web runs inside the TMIS shell
 * iframe (hosted URL), the XHR will 404 and `info` will be undefined.
 * In that case the shell must inject `window.appInfo` before init.
 */
function fetchAppInfoData(): Promise<void> {
  return new Promise<void>((resolve) => {
    const windowExt = window as unknown as IWindowWebOs;

    // If the shell already injected appInfo, nothing to do.
    if (windowExt.appInfo !== undefined && windowExt.appInfo !== null) {
      Log.i(
        TAG,
        "fetchAppInfoData(): window.appInfo already present, skipping fetch."
      );
      resolve();
      return;
    }

    const webOS = windowExt.webOS;

    if (webOS === undefined || webOS === null) {
      Log.w(TAG, "fetchAppInfoData(): webOS SDK not available, skipping.");
      resolve();
      return;
    }

    const guard: ISettleGuard = createSettleGuard(
      "fetchAppInfoData():",
      resolve
    );

    try {
      const rootPath: string = webOS.fetchAppRootPath();
      if (rootPath.length === 0) {
        if (guard.trySettle()) {
          Log.w(
            TAG,
            "fetchAppInfoData(): fetchAppRootPath returned empty " +
              "(hosted app, not packaged IPK). Skipping fetchAppInfo."
          );
        }
        resolve();
        return;
      }

      const appInfoPath: string = rootPath + "appinfo.json";

      webOS.fetchAppInfo((info: Record<string, unknown> | undefined) => {
        if (guard.trySettle()) {
          if (info !== undefined && info !== null) {
            windowExt.appInfo = info;
            Log.i(TAG, "fetchAppInfoData(): App info retrieved successfully.");
          } else {
            Log.w(
              TAG,
              "fetchAppInfoData(): appinfo.json not found or could not be parsed."
            );
          }
          resolve();
        }
      }, appInfoPath);
    } catch (e: unknown) {
      if (guard.trySettle()) {
        Log.e(
          TAG,
          "fetchAppInfoData(): Exception calling webOS.fetchAppInfo.",
          e
        );
        resolve();
      }
    }
  });
}

/**
 * Bootstrap webOS device data for TMIS LG shell.
 *
 * In the TMIS architecture, the web app itself is responsible for
 * calling webOS Luna services to populate `window.deviceId` and
 * `window.deviceInfo`. The shell only provides `shellAppVersion`
 * and `parentLocalStorage`.
 *
 * This function:
 * 1. Injects the webOSTVjs v1.1.0 SDK (identical to TMIS
 *    `injectdevicejs.js`) if `window.webOS` is not already available
 * 2. Fetches the LGUDID → `window.deviceId`
 * 3. Fetches device info → `window.deviceInfo`
 * 4. Fetches app info → `window.appInfo`
 *
 * Must be called early in the TMIS shell initialization path,
 * before TvProperties reads `window.deviceId` / `window.deviceInfo`.
 */
export async function initWebOsDeviceData(): Promise<void> {
  const windowExt = window as unknown as IWindowWebOs;

  // Inject the webOSTVjs SDK if window.webOS is not already
  // provided by the runtime.
  const sdkAvailable: boolean = loadWebOsSdk();
  if (!sdkAvailable) {
    Log.w(
      TAG,
      "initWebOsDeviceData(): webOS SDK not available and could not be injected. " +
        "Device data will not be populated."
    );
    return;
  }

  // Set window.getOSVersion so TvProperties can read the webOS version.
  windowExt.getOSVersion = detectWebOsVersion;
  Log.i(
    TAG,
    "initWebOsDeviceData(): webOS version detected as " +
      detectWebOsVersion() +
      "."
  );

  Log.i(TAG, "initWebOsDeviceData(): Starting webOS device data collection.");

  // Fetch deviceId first, then deviceInfo (which includes deviceId),
  // then app info.
  await fetchDeviceId();
  await fetchDeviceInfo();
  await fetchAppInfoData();

  Log.i(
    TAG,
    "initWebOsDeviceData(): Complete — deviceId=" +
      (windowExt.deviceId || "(not set)") +
      ", deviceInfo=" +
      (windowExt.deviceInfo !== undefined ? "set" : "(not set)") +
      ", appInfo=" +
      (windowExt.appInfo !== undefined ? "set" : "(not set)")
  );
}

/**
 * Call `webOS.platformBack()` to exit the application.
 *
 * This is the LG-approved exit mechanism that shows the webOS
 * recents list. Using `window.close()` would get the app rejected
 * from the LG Content Store.
 */
export function webOsPlatformBack(): void {
  const windowExt = window as unknown as IWindowWebOs;

  // Ensure the SDK is loaded if not already present.
  if (windowExt.webOS === undefined || windowExt.webOS === null) {
    loadWebOsSdk();
  }

  if (
    windowExt.webOS !== undefined &&
    windowExt.webOS !== null &&
    typeof windowExt.webOS.platformBack === "function"
  ) {
    Log.i(TAG, "webOsPlatformBack(): Calling webOS.platformBack().");
    windowExt.webOS.platformBack();
  } else {
    Log.w(TAG, "webOsPlatformBack(): webOS.platformBack not available.");
  }
}
